import {
  GridColDef,
  GridRowId,
  GridRowParams,
  GridSortModel,
  GridValidRowModel,
  useGridApiRef,
  GridSortItem,
} from '@mui/x-data-grid-pro';
import { useDrawerStore } from '@nexelem/drawer';
import { ContextedTable, SKELETON_ROWS_COUNT, PAGE_SIZE } from '@nexelem/table';
import { LoaderResult } from '@nexelem/navigation';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { omitBy, isEmpty, isEqual } from 'lodash';
import { defaultsDeep } from 'lodash/fp';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLoaderData } from 'react-router-dom';
import { SearchParamsProps, useQueryParams, useTableProps, useSyncOrderingWithStore } from './hooks';
import { FetchingResult, FilteredGridPageProps, TypedFiltersProps } from './types';
import { useTranslation } from 'react-i18next';
import { countRelevantRows, calculateLoadedCount, createSkeletonRows } from './utils';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { wait } from '@nexelem/utils';
import { queryClient } from '../../../services/queryClient';
import { format } from 'date-fns';

export function FilteredGridPage<FetchResult extends FetchingResult, Columns extends GridValidRowModel>({
  queryKey,
  defaultSortColumn,
  pinnedColumns,
  columns,
  fetchData,
  title = '',
  TableLegend,
  onClickRow,
  Filters,
  isAscendingSort = true,
  withSearchParams = false,
  tableProps: parentTableProps = {},
  defaultFilters = {},
  showEmptyTable = false,
  Header = () => null,
  EmptyState = () => null,
  onResetActiveFilter,
  columnHeaderHeight = 56,
}: FilteredGridPageProps<FetchResult, Columns>) {
  const { t } = useTranslation();
  const apiRef = useGridApiRef();
  const queryKeyArray = useMemo(() => (Array.isArray(queryKey) ? queryKey : [queryKey]), [queryKey]);
  const [selectedRowId, setSelectedRowId] = useState<GridRowId | null>(null);
  // Disable infinity scroll to export all data
  const [disableInfinityScroll, setDisableInfinityScroll] = useState(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const useFilterStore = useMemo(
    () =>
      withSearchParams
        ? useQueryParams<SearchParamsProps<TypedFiltersProps['data']>>
        : useState<SearchParamsProps<TypedFiltersProps['data']>>,
    [withSearchParams],
  );

  const initFilters = {
    data: defaultFilters,
    ordering: defaultSortColumn ? { column: defaultSortColumn, asc: isAscendingSort } : undefined,
  };

  const [filters, onChangeQueryFilters] = useFilterStore(initFilters, (newFilters) => {
    const columnsNames = (columns || []).map(({ field }) => field);
    if (newFilters.ordering?.column && !columnsNames.includes(newFilters.ordering.column)) {
      return {
        ...newFilters,
        ordering: {
          column: defaultSortColumn || (columns && columns[0]?.field.toString()) || '',
          asc: newFilters.ordering.asc,
        },
      };
    }
    return newFilters;
  });

  const loaderData = useLoaderData() as LoaderResult;

  const onCloseAllDrawerExceptMenu = useDrawerStore((state) => state.onCloseAllDrawerExceptMenu);

  const listQueryKey = useMemo(
    () => [...queryKeyArray, 'fetch', JSON.stringify(filters), disableInfinityScroll],
    [queryKeyArray, filters, disableInfinityScroll],
  );

  const onResetFilters = useCallback(() => {
    onChangeQueryFilters((prevFilters) => {
      const defaultsData = {
        ...defaultsDeep(defaultFilters),
      };
      const omitData = omitBy(defaultsData, ({ value }) => (typeof value === 'boolean' ? false : !value));
      return {
        ...prevFilters,
        data: omitData,
      };
    });
    onResetActiveFilter?.();
  }, [defaultFilters, onChangeQueryFilters, onResetActiveFilter]);

  const { isRowSelectable: customIsRowSelectable, ...restParentTableProps } = parentTableProps;

  // Sync currentOrdering in store whenever filters.ordering changes
  useSyncOrderingWithStore(parentTableProps.store, filters.ordering);

  const tableProps = useTableProps(defaultSortColumn, {
    isRowSelectable: (params) => {
      if (params.row._isSkeleton) {
        return false;
      }
      if (customIsRowSelectable) {
        return customIsRowSelectable(params);
      }
      return true;
    },
    ...restParentTableProps,
    onResetFilters,
  });

  const isTreeTable = Boolean(tableProps.treeData || parentTableProps.treeData);
  const countRelevantRowsFn = countRelevantRows(isTreeTable);
  const calculateLoadedCountFn = calculateLoadedCount(countRelevantRowsFn);

  const { data, fetchNextPage, hasNextPage, isFetching, isLoading } = useInfiniteQuery({
    queryKey: listQueryKey,
    initialPageParam: 0,
    staleTime: 1000 * 60 * 5,
    queryFn: ({ pageParam = 0 }) =>
      fetchData({
        ...filters,
        take: disableInfinityScroll ? undefined : (pageParam + 1) * PAGE_SIZE,
        skip: disableInfinityScroll ? undefined : pageParam * PAGE_SIZE,
      }).then((data) => {
        if (disableInfinityScroll) {
          useExportStatusStore.getState().setIsFullLoaded(true);
        }

        return data;
      }),
    getNextPageParam: (lastPage, allPages) => {
      const total = lastPage.result?.total ?? 0;
      const loadedCount = calculateLoadedCountFn(allPages);
      return loadedCount < total ? allPages.length : undefined;
    },
    select: (data) => {
      const allRows = data.pages.flatMap((page) => page.result?.data ?? []);
      const total = data.pages[0]?.result?.total ?? 0;
      const loadedCount = calculateLoadedCountFn(data.pages);
      const hasMoreData = loadedCount < total;

      if (allRows.length > 0 && hasMoreData) {
        return {
          rows: [...allRows, ...createSkeletonRows(SKELETON_ROWS_COUNT)],
          total,
        };
      }

      return { rows: allRows, total };
    },
  });

  const onRowsScrollEnd = useCallback(() => {
    if (hasNextPage && !isFetching) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetching]);

  useEffect(() => onCloseAllDrawerExceptMenu, [onCloseAllDrawerExceptMenu]);

  const onChangeFilters = useCallback(
    (newFilters: TypedFiltersProps['data']) => {
      onChangeQueryFilters((prevFilters) => {
        // we want override filters keys if exist in new filters
        const defaultsData = {
          ...defaultsDeep(defaultFilters, newFilters),
          ...newFilters,
        };
        const omitData = omitBy(defaultsData, ({ value }) => (typeof value === 'boolean' ? false : !value));
        return {
          ...prevFilters,
          data: omitData,
        };
      });
    },
    [defaultFilters, onChangeQueryFilters],
  );

  const onSortModelChange = useCallback(
    (model: GridSortModel) => {
      if (model.length) {
        const { field: column, sort } = model[0] as GridSortItem;
        const asc = sort === 'asc';
        onChangeQueryFilters((prevState) => {
          return { ...prevState, ordering: { column, asc } };
        });
      }
    },
    [onChangeQueryFilters],
  );

  const { data: loaderDataTitle } = useQuery({
    queryKey: ['filtered-grid-title', queryKey],
    queryFn: () => loaderData?.getTitle?.() || null,
  });

  const loaderTitle = loaderDataTitle && t(loaderDataTitle);

  const isNoAdditionalFiltersApplied = isEmpty(filters.data) ? true : isEqual(filters.data, defaultFilters);
  const shouldShowTable =
    showEmptyTable || isLoading || isFetching || !!data?.rows.length || !isNoAdditionalFiltersApplied;
  const shouldShowEmptyState = !shouldShowTable && isNoAdditionalFiltersApplied;

  // Use custom selection model only if not provided in tableProps and onClickRow is defined
  const customSelectionModel = useMemo(() => {
    if (tableProps.customSelectionModel) {
      return tableProps.customSelectionModel;
    }
    if (onClickRow && selectedRowId !== null) {
      return new Map([[selectedRowId, {}]]);
    }
    return undefined;
  }, [tableProps.customSelectionModel, onClickRow, selectedRowId]);

  const handleRowClick = useCallback(
    (params: GridRowParams) => {
      setSelectedRowId((prevId) => (prevId === params.id ? null : params.id));
      onClickRow?.(params);
    },
    [onClickRow],
  );

  const exportToCsv = async () => {
    setDisableInfinityScroll(true);

    await new Promise((resolve) => {
      const newQueryKey = [...listQueryKey];
      newQueryKey.splice(-1);
      const state = queryClient.getQueryState([...newQueryKey, true]);

      if (state?.status === 'success') {
        resolve(true);
      }

      const unsub = useExportStatusStore.subscribe((state) => {
        unsub();
        state.isFullLoaded && resolve(true);
      });
    });

    await wait(1000);
    apiRef.current.exportDataAsCsv({
      utf8WithBom: true,
      fileName: formatFileName(t(title).replace(' ', '_')),
      fields: columns
        .map(({ field }) => field.toString())
        .filter((field) => !['leftPadding', 'rightPadding', 'status-custom', 'name-custom'].includes(field)),
    });

    setDisableInfinityScroll(false);
    useExportStatusStore.getState().setIsFullLoaded(false);
  };

  return (
    <>
      {shouldShowTable && (
        <div className="flex h-full flex-col items-stretch">
          <div className="px-6">
            <Header filters={filters.data} title={loaderTitle || title} onFiltersChange={onChangeFilters} />
            {Filters && (
              <div className="pb-4">
                <Filters exportToCsv={exportToCsv} filters={filters.data} onChange={onChangeFilters} />
              </div>
            )}
          </div>
          <ContextedTable
            apiRef={apiRef}
            columnHeaderHeight={columnHeaderHeight}
            columns={columns as Array<GridColDef<Columns>>}
            customSelectionModel={customSelectionModel}
            isLoading={isLoading}
            pinnedColumns={pinnedColumns}
            rowCount={data?.total || 0}
            rows={data?.rows || []}
            TableLegend={TableLegend}
            sortModel={
              filters.ordering?.column
                ? [{ field: filters.ordering.column, sort: filters.ordering.asc ? 'asc' : 'desc' }]
                : undefined
            }
            onResetFilters={onResetFilters}
            onRowClick={handleRowClick}
            onRowsScrollEnd={onRowsScrollEnd}
            onSortModelChange={onSortModelChange}
            {...tableProps}
          />
        </div>
      )}
      {shouldShowEmptyState && <EmptyState title={loaderTitle || title} />}
    </>
  );
}

type ExportStatusStore = {
  isFullLoaded: boolean;
  setIsFullLoaded: (isFullLoaded: boolean) => void;
};

const useExportStatusStore = create(
  immer<ExportStatusStore>((set) => {
    return {
      isFullLoaded: false,
      setIsFullLoaded: (isFullLoaded) =>
        set((state) => {
          state.isFullLoaded = isFullLoaded;
        }),
    };
  }),
);

const formatFileName = (name: string) => {
  const date = format(new Date(), 'dd-MMM-yyyy_HH-mm');
  return `${name}_${date}`;
};

export * from './FilteredGridPage';
